import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, TextField, Button, Typography, Paper, Box,
    CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
    Checkbox, FormControlLabel, Tabs, Tab, Alert, useTheme, useMediaQuery
} from '@mui/material';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
    : null;

function TabPanel({ children, value, index, ...other }) {
    const isActive = value === index;

    return (
        <Box
            role="tabpanel"
            hidden={!isActive}
            id={`account-settings-tabpanel-${index}`}
            aria-labelledby={`account-settings-tab-${index}`}
            sx={{
                width: '100%',
                height: '100%',
                overflowY: 'auto',
                flexGrow: 1,
                display: isActive ? 'block' : 'none',
            }}
            {...other}
        >
            {isActive && <Box sx={{ width: '100%', pt: 2 }}>{children}</Box>}
        </Box>
    );
}

const a11yProps = (index) => ({
    id: `account-settings-tab-${index}`,
    'aria-controls': `account-settings-tabpanel-${index}`,
});

const AccountSettings = ({ isDialog = false, open = false, onClose }) => {
    const token = useSelector((state) => state.auth.token);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('success');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);
    const [tabIndex, setTabIndex] = useState(0);
    const [userInfo, setUserInfo] = useState(null);
    const [subscription, setSubscription] = useState(null);
    const [loadingSubscription, setLoadingSubscription] = useState(true);
    const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);
    const [isProcessingPortal, setIsProcessingPortal] = useState(false);
    const [notifPrefs, setNotifPrefs] = useState({
        emailNotifications: true,
        pushNotifications: false,
        summaryFrequencyHours: 12,
    });
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const setFeedbackMessage = useCallback((text, type = 'success') => {
        setMessage(text);
        setMessageType(type);
    }, []);

    // Fetch user data
    const fetchUserData = useCallback(async () => {
        if (!token) return null;
        setLoadingSubscription(true);
        try {
            const response = await axios.get('/api/users/me', {
                headers: { Authorization: `Bearer ${token}` },
            });
            const {
                twoFactorEnabled: tfa,
                username,
                email,
                accountType,
                subscription: subscriptionData,
            } = response.data;
            if (typeof tfa === 'boolean') setTwoFactorEnabled(tfa);
            setUserInfo({ username, email, accountType });
            setSubscription(subscriptionData || null);
            setLoadingSubscription(false);
            return response.data;
        } catch (err) {
            console.error('Error fetching data:', err);
            setLoadingSubscription(false);
            setFeedbackMessage('Error fetching account data.', 'error');
            return null;
        }
    }, [token, setFeedbackMessage]);

    useEffect(() => {
        fetchUserData();
    }, [fetchUserData]);

    useEffect(() => {
        if (isDialog && open) {
            fetchUserData();
        }
    }, [isDialog, open, fetchUserData]);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await axios.get('/api/user-settings', { headers: { Authorization: `Bearer ${token}` } });
                if (res.data.notificationPreferences) {
                    setNotifPrefs(res.data.notificationPreferences);
                }
            } catch (err) {
                console.error('Error fetching settings:', err);
            }
        };
        fetchSettings();
    }, [token]);

    const toggleTwoFactor = async (enabled) => {
        try {
            if (enabled) {
                await axios.post('/api/users/enable-2fa', {}, { headers: { Authorization: `Bearer ${token}` } });
            } else {
                await axios.post('/api/users/disable-2fa', {}, { headers: { Authorization: `Bearer ${token}` } });
            }
            setTwoFactorEnabled(enabled);
            setFeedbackMessage(`Two-factor authentication ${enabled ? 'enabled' : 'disabled'}.`, 'success');
        } catch (err) {
            console.error('Error updating 2FA:', err);
            setFeedbackMessage('Error updating 2FA settings.', 'error');
        }
    };

    const handleCheckout = useCallback(async () => {
        setIsProcessingCheckout(true);
        setMessage('');
        try {
            const dashboardUrl = `${window.location.origin}/#/dashboard`;
            const successUrl = `${dashboardUrl}?checkoutSuccess=true&session_id={CHECKOUT_SESSION_ID}`;
            const cancelUrl = `${dashboardUrl}?checkoutCanceled=true`;

            const { data } = await axios.post('api/billing/checkout', {
                successUrl,
                cancelUrl,
            }, {
                headers: { Authorization: `Bearer ${token}` },
            });

            const redirectWithStripe = async () => {
                if (stripePromise && data.id) {
                    const stripe = await stripePromise;
                    if (stripe) {
                        const { error } = await stripe.redirectToCheckout({ sessionId: data.id });
                        if (!error) {
                            return true;
                        }
                        console.error('Stripe redirect error:', error);
                    }
                }
                return false;
            };

            const handledByStripe = await redirectWithStripe();
            if (!handledByStripe) {
                if (data.url) {
                    window.location.href = data.url;
                    return;
                }
                setFeedbackMessage('Unable to start checkout session. Please try again later.', 'error');
            }
        } catch (err) {
            console.error('Error creating checkout session:', err);
            const errorMessage = err.response?.data?.error || 'Unable to start checkout session.';
            setFeedbackMessage(errorMessage, 'error');
        } finally {
            setIsProcessingCheckout(false);
        }
    }, [token, setFeedbackMessage]);

    const handleManageBilling = useCallback(async () => {
        setIsProcessingPortal(true);
        setMessage('');
        try {
            const returnUrl = `${window.location.origin}/#/dashboard`;
            const { data } = await axios.post('api/billing/portal', { returnUrl }, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (data.url) {
                window.location.href = data.url;
                return;
            }
            setFeedbackMessage('Unable to open the billing portal.', 'error');
        } catch (err) {
            console.error('Error opening billing portal:', err);
            const errorMessage = err.response?.data?.error || 'Unable to open the billing portal.';
            setFeedbackMessage(errorMessage, 'error');
        } finally {
            setIsProcessingPortal(false);
        }
    }, [token, setFeedbackMessage]);

    const formatStatus = (status) => {
        if (!status) return 'Not subscribed';
        return status
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const parseDateValue = (value) => {
        if (!value) return null;
        if (value instanceof Date && !Number.isNaN(value.getTime())) {
            return value;
        }

        const numericValue = Number(value);
        if (!Number.isNaN(numericValue)) {
            const timestamp = numericValue < 1e12 ? numericValue * 1000 : numericValue;
            const asDate = new Date(timestamp);
            return Number.isNaN(asDate.getTime()) ? null : asDate;
        }

        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    const subscriptionStatus = subscription?.status || subscription?.subscriptionStatus || null;
    const renewalDateValue =
        subscription?.currentPeriodEnd ??
        subscription?.current_period_end ??
        null;
    const renewalDate = parseDateValue(renewalDateValue);
    let planLabel =
        subscription?.planName ||
        subscription?.plan ||
        subscription?.priceNickname ||
        subscription?.priceName ||
        null;
    if (!planLabel && subscriptionStatus) {
        planLabel = 'Active subscription';
    }
    if (!planLabel) {
        planLabel = userInfo?.accountType || 'Not subscribed';
    }

    const renewalText = renewalDate
        ? `Renews on: ${renewalDate.toLocaleDateString()} at ${renewalDate.toLocaleTimeString()}`
        : subscriptionStatus
            ? 'No upcoming renewal date'
            : 'No active subscription';
    const canManageBilling = Boolean(subscriptionStatus);


    const tabItems = [
        {
            label: 'General',
            icon: <PersonOutlineOutlinedIcon fontSize="small" />,
        },
        {
            label: 'Notifications',
            icon: <NotificationsNoneOutlinedIcon fontSize="small" />,
        },
        {
            label: 'Security',
            icon: <ShieldOutlinedIcon fontSize="small" />,
        },
        {
            label: 'Account',
            icon: <CreditCardOutlinedIcon fontSize="small" />,
        },
    ];

    const content = (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                gap: 2,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isSmallScreen ? 'column' : 'row',
                    gap: { xs: 2, sm: 3 },
                    flex: 1,
                    minHeight: 0,
                }}
            >
                <Tabs
                    orientation={isSmallScreen ? 'horizontal' : 'vertical'}
                    variant="scrollable"
                    value={tabIndex}
                    onChange={(e, v) => setTabIndex(v)}
                    aria-label="Account settings navigation"
                    sx={{
                        borderRight: isSmallScreen ? 0 : 1,
                        borderBottom: isSmallScreen ? 1 : 0,
                        borderColor: 'divider',
                        minWidth: isSmallScreen ? 'auto' : 180,
                        alignItems: 'flex-start',
                        px: isSmallScreen ? 0 : 1,
                        py: 1,
                    }}
                >
                    {tabItems.map((tab, index) => (
                        <Tab
                            key={tab.label}
                            label={tab.label}
                            icon={tab.icon}
                            iconPosition="start"
                            {...a11yProps(index)}
                            sx={{
                                justifyContent: 'flex-start',
                                textAlign: 'left',
                                px: 1.5,
                                py: 1,
                                minHeight: 48,
                                gap: 1.5,
                                borderRadius: 1,
                                typography: 'body2',
                                '&.Mui-selected': {
                                    backgroundColor: 'action.selected',
                                },
                            }}
                        />
                    ))}
                </Tabs>
                <Box
                    sx={{
                        flexGrow: 1,
                        pl: isSmallScreen ? 0 : 3,
                        minHeight: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0,
                    }}
                >
                    <TabPanel value={tabIndex} index={0}>
                        {userInfo ? (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Profile Information
                                </Typography>
                                <Typography variant="body1">
                                    Username: {userInfo.username}
                                </Typography>
                                <Typography variant="body1">
                                    Email: {userInfo.email}
                                </Typography>
                                <Typography variant="body1">
                                    Account Type: {userInfo.accountType || 'Standard'}
                                </Typography>
                            </Box>
                        ) : (
                            <CircularProgress />
                        )}
                    </TabPanel>
                    <TabPanel value={tabIndex} index={1}>
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={notifPrefs.emailNotifications}
                                    onChange={(e) =>
                                        setNotifPrefs({
                                            ...notifPrefs,
                                            emailNotifications: e.target.checked,
                                        })
                                    }
                                />
                            )}
                            label="Email Notifications"
                        />
                        <FormControlLabel
                            control={(
                                <Checkbox
                                    checked={notifPrefs.pushNotifications}
                                    onChange={(e) =>
                                        setNotifPrefs({
                                            ...notifPrefs,
                                            pushNotifications: e.target.checked,
                                        })
                                    }
                                />
                            )}
                            label="Push Notifications"
                        />
                        <TextField
                            label="Summary Email Frequency (hours)"
                            type="number"
                            value={notifPrefs.summaryFrequencyHours}
                            onChange={(e) =>
                                setNotifPrefs({
                                    ...notifPrefs,
                                    summaryFrequencyHours:
                                        parseInt(e.target.value, 10) || 0,
                                })
                            }
                            fullWidth
                            sx={{ mt: 2 }}
                        />
                        <Button
                            sx={{ mt: 2 }}
                            variant="contained"
                            onClick={async () => {
                                try {
                                    await axios.put(
                                        '/api/user-settings/notifications',
                                        notifPrefs,
                                        {
                                            headers: {
                                                Authorization: `Bearer ${token}`,
                                            },
                                        },
                                    );
                                    setFeedbackMessage(
                                        'Notification settings saved.',
                                        'success',
                                    );
                                } catch (err) {
                                    console.error('Error saving notification settings:', err);
                                }
                            }}
                        >
                            Save Notification Settings
                        </Button>
                    </TabPanel>
                    <TabPanel value={tabIndex} index={2}>
                        <Typography variant="h6" gutterBottom>
                            Two-Factor Authentication
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={twoFactorEnabled}
                                    onChange={(e) => toggleTwoFactor(e.target.checked)}
                                />
                            }
                            label="Enable two-factor authentication"
                        />
                    </TabPanel>
                    <TabPanel value={tabIndex} index={3}>
                        {userInfo ? (
                            <Box>
                                <Typography variant="h6" gutterBottom>
                                    Subscription & Billing
                                </Typography>
                                {loadingSubscription ? (
                                    <CircularProgress size={24} />
                                ) : (
                                    <Box>
                                        <Typography variant="body1">
                                            Current plan: {planLabel}
                                        </Typography>
                                        <Typography variant="body1">
                                            Status: {formatStatus(subscriptionStatus)}
                                        </Typography>
                                        <Typography variant="body1">
                                            {renewalText}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 2,
                                                mt: 2,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                onClick={handleCheckout}
                                                disabled={isProcessingCheckout}
                                            >
                                                {isProcessingCheckout
                                                    ? 'Redirecting…'
                                                    : 'Subscribe'}
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                onClick={handleManageBilling}
                                                disabled={
                                                    isProcessingPortal ||
                                                    !canManageBilling
                                                }
                                            >
                                                {isProcessingPortal
                                                    ? 'Opening…'
                                                    : 'Manage billing'}
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                                <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                                    Close Account
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Permanently remove your account and all associated
                                    data.
                                </Typography>
                                <Button
                                    sx={{ mt: 2 }}
                                    color="error"
                                    variant="contained"
                                    onClick={() => {
                                        setDeleteDialogOpen(true);
                                        setConfirmText('');
                                    }}
                                >
                                    Delete Account
                                </Button>
                                <Dialog
                                    open={deleteDialogOpen}
                                    onClose={() => setDeleteDialogOpen(false)}
                                >
                                    <DialogTitle>Confirm Account Deletion</DialogTitle>
                                    <DialogContent>
                                        <Typography gutterBottom>
                                            Type <strong>DELETE</strong> to confirm you
                                            want to permanently remove your account.
                                        </Typography>
                                        <TextField
                                            autoFocus
                                            margin="dense"
                                            label="Type DELETE to confirm"
                                            fullWidth
                                            value={confirmText}
                                            onChange={(e) =>
                                                setConfirmText(e.target.value)
                                            }
                                        />
                                    </DialogContent>
                                    <DialogActions>
                                        <Button onClick={() => setDeleteDialogOpen(false)}>
                                            Cancel
                                        </Button>
                                        <Button
                                            color="error"
                                            disabled={confirmText !== 'DELETE'}
                                            onClick={async () => {
                                                try {
                                                    await axios.delete(
                                                        '/api/users/me',
                                                        {
                                                            headers: {
                                                                Authorization: `Bearer ${token}`,
                                                            },
                                                        },
                                                    );
                                                    setFeedbackMessage(
                                                        'Account deleted',
                                                        'success',
                                                    );
                                                } catch (err) {
                                                    console.error(
                                                        'Error deleting account:',
                                                        err,
                                                    );
                                                    setFeedbackMessage(
                                                        'Error deleting account',
                                                        'error',
                                                    );
                                                } finally {
                                                    setDeleteDialogOpen(false);
                                                }
                                            }}
                                        >
                                            Delete
                                        </Button>
                                    </DialogActions>
                                </Dialog>
                            </Box>
                        ) : (
                            <CircularProgress />
                        )}
                    </TabPanel>
                </Box>
            </Box>
            {message && (
                <Alert severity={messageType} sx={{ mt: 2 }}>
                    {message}
                </Alert>
            )}
        </Box>
    );

    if (isDialog) {
        return (
            <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
                <DialogTitle>Account Settings</DialogTitle>
                <DialogContent dividers>{content}</DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    return (
        <Container
            maxWidth={false}
            sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}
        >
            <Paper
                elevation={3}
                sx={{
                    width: 580,
                    height: 600,
                    p: 4,
                    borderRadius: 3,
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                }}
            >
                <Typography variant="h4" gutterBottom align="left">
                    Account Settings
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0 }}>{content}</Box>
            </Paper>
        </Container>
    );
};

export default AccountSettings;
